import client from './client';
import { toRawListParams } from './paging';
import { PaginatedResponse, ListParams } from '../types/index';

export interface AccessGroup {
  id: string;
  entra_object_id: string;
  display_name: string;
  description?: string | null;
  roles: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
  last_synced_at?: string | null;
  last_sync_member_count?: string | null;
  last_sync_error?: string | null;
}

export interface AccessGroupCreatePayload {
  entra_object_id: string;
  display_name: string;
  description?: string | null;
  roles: string[];
}

export interface AccessGroupUpdatePayload {
  display_name?: string;
  description?: string | null;
  roles?: string[];
  is_active?: boolean;
}

export interface AvailableRole {
  value: string;
  label: string;
  description: string;
}

// The paginated envelope as the backend actually sends it (`PaginatedResponse`
// in `app/schemas/common.py`). Kept snake_case to match the models above.
interface RawPaginatedAccessGroups {
  items: AccessGroup[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export const getAccessGroups = async (
  params?: ListParams & { include_inactive?: boolean; role?: string }
): Promise<PaginatedResponse<AccessGroup>> => {
  // WI #337: `AccessSettings.tsx` asks for `{ page: 1, pageSize: 100 }`. That
  // was forwarded verbatim, `pageSize` did not bind to `PaginationParams`, and
  // the page silently showed the server default of 20 groups.
  const { include_inactive, role, ...listParams } = params || {};
  // WI #323: the models in this module are snake_case by design (convention 2
  // in CLAUDE.md), but `PaginatedResponse` is not — it is the camelCase
  // frontend envelope, with `pageSize`/`pages`, while the backend sends
  // `page_size`/`total_pages`. Typing the raw response as `PaginatedResponse`
  // left both fields `undefined`; it looked fine only because the one consumer
  // reads `items`, which is spelled the same on both sides. The envelope is
  // unwrapped explicitly here, as every other list endpoint does.
  const response = await client.get<RawPaginatedAccessGroups>('/access-groups', {
    params: {
      ...(toRawListParams(listParams) || {}),
      ...(include_inactive !== undefined ? { include_inactive } : {}),
      ...(role !== undefined ? { role } : {}),
    },
  });
  const data = response.data;
  return {
    items: data.items || [],
    total: data.total,
    page: data.page,
    pageSize: data.page_size,
    pages: data.total_pages,
  };
};

export const getAvailableRoles = async (): Promise<AvailableRole[]> => {
  const response = await client.get<AvailableRole[]>('/access-groups/available-roles');
  return response.data;
};

export const createAccessGroup = async (
  payload: AccessGroupCreatePayload
): Promise<AccessGroup> => {
  const response = await client.post<AccessGroup>('/access-groups', payload);
  return response.data;
};

export const updateAccessGroup = async (
  id: string,
  payload: AccessGroupUpdatePayload
): Promise<AccessGroup> => {
  const response = await client.patch<AccessGroup>(`/access-groups/${id}`, payload);
  return response.data;
};

export const deleteAccessGroup = async (id: string): Promise<void> => {
  await client.delete(`/access-groups/${id}`);
};
